// copy-register: commercial
import { Link, useParams } from "react-router-dom";
import { PlaceTrail } from "../components/PlaceTrail";
import {
  businessModulePath,
  domainWorkblockPath,
  groundworksEntityPath,
  integrationExamplePath,
  integrationRecipePath,
  integrationSystemPath,
  workblockSourceUrl
} from "../catalogue";
import { useCopy, useFramework } from "../framework";
import { AbsenceBlock, SectionHeading, StatusMark, humanize } from "../components/ui";
import { usePageTitle } from "./support";
import { useAssetManifest } from "../downloadsManifest";
import { AssetFormats, InlineLinks, ProofMark, replaceTokens } from "./ConnectedWorkPage";

/**
 * Canonical Connected Work record pages. Each governed integration object
 * — outcome tool, system profile, mapping profile and standard/format —
 * owns one stable route; /connected-work stays the recipe-led explanation
 * and search that links here. Every page reports recipe-class proof, never
 * live connector, tenant or runtime state.
 */

function RecordAbsence({ kind }: { kind: string }) {
  const copy = useCopy();
  return (
    <div className="apf-page apf-register-marketing apf-connected-work-record">
      <AbsenceBlock
        eyebrow={copy("page.connected-work.record.absence-eyebrow", "Connected Work reference")}
        title={copy("page.connected-work.record.absence-title", "Record not found")}
        support={copy("page.connected-work.record.absence-support", "This record is not part of the current framework release.")}
        headingLevel={1}
        holds={[{ label: humanize(kind), count: 0 }]}
        routes={<Link to="/connected-work">{copy("page.connected-work.record.absence-route", "Browse Connected Work")}</Link>}
      />
    </div>
  );
}

function RecordShell({ eyebrow, title, proof, aside, children }: {
  eyebrow: string;
  title: string;
  proof?: React.ReactNode;
  aside?: React.ReactNode;
  children: React.ReactNode;
}) {
  const copy = useCopy();
  usePageTitle(title);
  return (
    <div className="apf-page apf-register-marketing apf-connected-work-record">
      <PlaceTrail stops={[
        { label: "Framework", to: "/" },
        { label: copy("page.connected-work.eyebrow", "Connected Work"), to: "/connected-work" },
        { label: title }
      ]} />
      <div className={aside ? "apf-detail-hero" : "apf-detail-hero apf-detail-hero--single"}>
        <section className="apf-detail-hero__main apf-surface">
          <p className="apf-page-header__eyebrow">{eyebrow}</p>
          <h1>{title}</h1>
          {proof}
        </section>
        {aside}
      </div>
      {children}
      <p className="apf-deep-link"><Link to="/connected-work">{copy("page.connected-work.record.back", "Back to Connected Work")}</Link></p>
    </div>
  );
}

/**
 * The canonical integration-recipe record. The catalogue index links here;
 * the page carries the full recipe contract with its own h1 and title, the
 * same anatomy the other four record families already own.
 */
export function IntegrationRecipeRecordPage() {
  const { recipeId } = useParams();
  const { snapshot } = useFramework();
  const copy = useCopy();
  const { manifest } = useAssetManifest();
  const integrations = snapshot.integrations;
  const recipe = integrations.recipes.find((candidate) => candidate.id === recipeId);
  if (!recipe) return <RecordAbsence kind="integration recipes" />;
  const domainById = new Map(snapshot.content.domainWorkblocks.map((domain) => [domain.id, domain]));
  const moduleById = new Map(snapshot.businessModuleRegistry.businessModules.map((module) => [module.id, module]));
  const entityById = new Map(snapshot.groundworks.entities.map((entity) => [entity.id, entity]));
  const channelById = new Map(integrations.channels.map((channel) => [channel.id, channel]));
  const formatById = new Map(integrations.formats.map((format) => [format.id, format]));
  const systemRoleById = new Map(integrations.systemRoles.map((role) => [role.id, role]));
  const standardById = new Map(snapshot.groundworks.standards.map((standard) => [standard.id, standard]));
  const asset = (manifest?.assets ?? []).find((candidate) => candidate.assetId === `recipe-${recipe.id}`);
  const relatedExamples = integrations.examples.filter((example) => example.recipeIds.includes(recipe.id));
  const relatedSystems = integrations.systemProfiles.filter((system) => system.recipeIds.includes(recipe.id));
  return (
    <RecordShell
      eyebrow={copy(`page.connected-work.recipe-direction.${recipe.direction}`)}
      title={recipe.label}
      proof={<p className="apf-recipe-detail__proof"><ProofMark proofClass={recipe.proofClass} /><AssetFormats asset={asset} /></p>}
    >
      <section className="apf-page-section">
        <SectionHeading title={recipe.outcome} />
        <dl className="apf-recipe-facts">
          <div><dt>{copy("page.connected-work.fact.trigger")}</dt><dd>{recipe.trigger}</dd></div>
          <div><dt>{copy("page.connected-work.fact.owner")}</dt><dd>{recipe.owner}</dd></div>
          <div><dt>{copy("page.connected-work.fact.canonical-path")}</dt><dd>{copy("page.connected-work.canonical-path")}</dd></div>
          <div><dt>{copy("page.connected-work.fact.authority")}</dt><dd>{recipe.authorityBoundary}</dd></div>
          <div><dt>{copy("page.connected-work.fact.inputs")}</dt><dd><InlineLinks values={recipe.inputEntityIds} resolve={(id) => ({ label: entityById.get(id)?.label ?? humanize(id), to: entityById.has(id) ? groundworksEntityPath(id) : undefined })} /></dd></div>
          <div><dt>{copy("page.connected-work.fact.outputs")}</dt><dd><InlineLinks values={recipe.outputEntityIds} resolve={(id) => ({ label: entityById.get(id)?.label ?? humanize(id), to: entityById.has(id) ? groundworksEntityPath(id) : undefined })} /></dd></div>
          <div><dt>{copy("page.connected-work.fact.action-classes")}</dt><dd><InlineLinks values={recipe.actionClasses} resolve={(id) => ({ label: copy(`page.connected-work.action-class.${id}`) })} /></dd></div>
          <div><dt>{copy("page.connected-work.fact.system-roles")}</dt><dd><InlineLinks values={recipe.systemRoleIds} resolve={(id) => ({ label: systemRoleById.get(id)?.label ?? humanize(id) })} /></dd></div>
          <div><dt>{copy("page.connected-work.fact.channels")}</dt><dd><InlineLinks values={recipe.channelIds} resolve={(id) => ({ label: channelById.get(id)?.label ?? humanize(id) })} /></dd></div>
          <div><dt>{copy("page.connected-work.fact.formats")}</dt><dd><InlineLinks values={recipe.formatIds} resolve={(id) => ({ label: formatById.get(id)?.label ?? humanize(id) })} /></dd></div>
          <div><dt>{copy("page.connected-work.fact.standards")}</dt><dd>{recipe.standardIds.length ? <InlineLinks values={recipe.standardIds} resolve={(id) => ({ label: standardById.get(id)?.label ?? humanize(id) })} /> : copy("page.connected-work.fact.no-standard")}</dd></div>
          <div><dt>{copy("page.connected-work.fact.records")}</dt><dd><InlineLinks values={recipe.entityIds.slice(0, 18)} resolve={(id) => ({ label: entityById.get(id)?.label ?? humanize(id), to: entityById.has(id) ? groundworksEntityPath(id) : undefined })} />{recipe.entityIds.length > 18 ? ` · ${recipe.entityIds.length - 18} ${copy("page.connected-work.related-records-suffix")}` : null}</dd></div>
          <div><dt>{copy("page.connected-work.fact.workblocks")}</dt><dd><InlineLinks values={recipe.domainWorkblockIds} resolve={(id) => ({ label: domainById.has(id) ? copy(domainById.get(id)!.nameKey) : humanize(id), to: domainById.has(id) ? domainWorkblockPath(id) : undefined })} /></dd></div>
          <div><dt>{copy("page.connected-work.fact.modules")}</dt><dd><InlineLinks values={recipe.businessModuleIds} resolve={(id) => { const module = moduleById.get(id); return { label: module ? copy(module.labelKey) : humanize(id), to: module ? businessModulePath(module.domainWorkblockId, id) : undefined }; }} /></dd></div>
        </dl>
        <div className="apf-reliability-grid">
          {Object.entries(recipe.reliability).map(([id, value]) => <article key={id}><h3>{copy(`page.connected-work.reliability.${id}`)}</h3><p>{value}</p></article>)}
        </div>
        <details className="apf-recipe-config"><summary>{copy("page.connected-work.configuration-title")}</summary><ul>{recipe.configurableFields.map((field) => <li key={field}>{field}</li>)}</ul></details>
        {relatedExamples.length ? <div className="apf-recipe-related"><h3>{copy("page.connected-work.related-examples-title")}</h3><ul>{relatedExamples.map((example) => <li key={example.id}><Link to={integrationExamplePath(example.id)}><ProofMark proofClass={example.proofClass} /> {example.label}</Link></li>)}</ul></div> : null}
        {relatedSystems.length ? <div className="apf-recipe-related apf-recipe-related--systems"><h3>{replaceTokens(copy("page.connected-work.representative-systems-summary"), { systems: relatedSystems.length })}</h3><p>{relatedSystems.map((system, index) => <span key={system.id}>{index ? " · " : null}<Link to={integrationSystemPath(system.id)}>{system.name}</Link></span>)}</p></div> : null}
      </section>
    </RecordShell>
  );
}

/**
 * The canonical illustrative-example record: one stable address per governed
 * example, with its proof class, authority boundary and related recipes.
 */
export function IntegrationExampleRecordPage() {
  const { exampleId } = useParams();
  const { snapshot } = useFramework();
  const copy = useCopy();
  const integrations = snapshot.integrations;
  const example = integrations.examples.find((candidate) => candidate.id === exampleId);
  if (!example) return <RecordAbsence kind="illustrative examples" />;
  const domain = example.domainWorkblockId
    ? snapshot.content.domainWorkblocks.find(({ id }) => id === example.domainWorkblockId)
    : undefined;
  const module = example.businessModuleId
    ? snapshot.businessModuleRegistry.businessModules.find(({ id }) => id === example.businessModuleId)
    : undefined;
  const tool = example.toolRecipeId
    ? integrations.toolRecipes.find(({ id }) => id === example.toolRecipeId)
    : undefined;
  const recipeById = new Map(integrations.recipes.map((recipe) => [recipe.id, recipe]));
  const entityById = new Map(snapshot.groundworks.entities.map((entity) => [entity.id, entity]));
  return (
    <RecordShell
      eyebrow={domain ? copy(domain.nameKey) : copy("page.connected-work.example.cross-cutting-label")}
      title={example.label}
      proof={<p className="apf-recipe-detail__proof"><ProofMark proofClass={example.proofClass} /></p>}
    >
      <section className="apf-page-section">
        {example.userQuestion ? <p className="apf-example-list__question">{example.userQuestion}</p> : null}
        {example.output ? <p>{example.output}</p> : <p>{example.summary}</p>}
        <dl className="apf-recipe-facts">
          <div><dt>{copy("page.connected-work.example.module-label")}</dt><dd>{module ? <Link to={businessModulePath(module.domainWorkblockId, module.id)}>{copy(module.labelKey)}</Link> : copy("page.connected-work.example.cross-domain")}{module ? <> · <StatusMark value={module.implementationStatus} /></> : null}</dd></div>
          <div><dt>{copy("page.connected-work.example.tool-proof-label")}</dt><dd>{tool ? <><ProofMark proofClass={tool.proofClass} /> · {copy(`page.connected-work.action-class.${tool.actionClass}`)}</> : copy("page.connected-work.example.not-applicable")}</dd></div>
          <div><dt>{copy("page.connected-work.example.authority-ceiling-label")}</dt><dd>{example.authorityBoundary}</dd></div>
          <div><dt>{copy("page.connected-work.example.owner-label")}</dt><dd>{example.owner}</dd></div>
          <div><dt>{copy("page.connected-work.example.client-configuration-label")}</dt><dd>{example.nextAction}</dd></div>
          {example.recipeIds.length ? <div><dt>{copy("page.connected-work.example.recipes-label")}</dt><dd><InlineLinks values={example.recipeIds} resolve={(id) => ({ label: recipeById.get(id)?.label ?? humanize(id), to: integrationRecipePath(id) })} /></dd></div> : null}
          {example.entityIds.length ? <div><dt>{copy("page.connected-work.example.records-label")}</dt><dd><InlineLinks values={example.entityIds} resolve={(id) => ({ label: entityById.get(id)?.label ?? humanize(id), to: entityById.has(id) ? groundworksEntityPath(id) : undefined })} /></dd></div> : null}
        </dl>
        {example.workblockPath ? <p><a href={workblockSourceUrl(example.workblockPath)} target="_blank" rel="noreferrer">{copy("page.connected-work.open-demo-workblock")}</a></p> : null}
        {example.demoReadiness ? <p className="apf-demo-readiness"><strong>{example.demoReadiness}</strong></p> : null}
        {example.flowSteps ? <details className="apf-recipe-config"><summary>{copy("page.connected-work.example-flow-label")}</summary><ol>{example.flowSteps.map((step) => <li key={step}>{step}</li>)}</ol></details> : null}
      </section>
    </RecordShell>
  );
}

export function IntegrationToolRecordPage() {
  const { toolId } = useParams();
  const { snapshot } = useFramework();
  const copy = useCopy();
  const { manifest } = useAssetManifest();
  const integrations = snapshot.integrations;
  const tool = integrations.toolRecipes.find((candidate) => candidate.id === toolId);
  if (!tool) return <RecordAbsence kind="outcome tools" />;
  const module = snapshot.businessModuleRegistry.businessModules.find(({ id }) => id === tool.businessModuleId);
  const entityById = new Map(snapshot.groundworks.entities.map((entity) => [entity.id, entity]));
  const recipeById = new Map(integrations.recipes.map((recipe) => [recipe.id, recipe]));
  const agentById = new Map(snapshot.businessModuleRegistry.businessModules.flatMap((candidate) => candidate.agents.map((agent) => [agent.id, agent] as const)));
  const asset = (manifest?.assets ?? []).find((candidate) => candidate.assetId === `outcome-tool-${tool.businessModuleId}`);
  return (
    <RecordShell
      eyebrow={copy("page.connected-work.record.tool-eyebrow", "Outcome-tool recipe")}
      title={tool.label}
      proof={<p className="apf-recipe-detail__proof"><ProofMark proofClass={tool.proofClass} /><AssetFormats asset={asset} /></p>}
    >
      <section className="apf-page-section">
        <SectionHeading title={tool.outcome} />
        <dl className="apf-recipe-facts">
          <div><dt>{copy("page.connected-work.tool.module-label")}</dt><dd>{module ? <Link to={businessModulePath(module.domainWorkblockId, module.id)}>{copy(module.labelKey)}</Link> : humanize(tool.businessModuleId)}</dd></div>
          <div><dt>{copy("page.connected-work.tool.maturity-label")}</dt><dd>{module ? <StatusMark value={module.implementationStatus} /> : copy("page.connected-work.tool.maturity-unavailable")}</dd></div>
          <div><dt>{copy("page.connected-work.tool.action-class-label")}</dt><dd>{copy(`page.connected-work.action-class.${tool.actionClass}`)}</dd></div>
          <div><dt>{copy("page.connected-work.tool.authority-label")}</dt><dd>{tool.authorityCeiling}</dd></div>
          <div><dt>{copy("page.connected-work.tool.agents-label")}</dt><dd><InlineLinks values={tool.agentIds} resolve={(id) => ({ label: agentById.get(id)?.label ?? humanize(id), to: module ? `${businessModulePath(module.domainWorkblockId, module.id)}#agent-${id}` : undefined })} /></dd></div>
          <div><dt>{copy("page.connected-work.tool.records-label")}</dt><dd><InlineLinks values={tool.entityIds} resolve={(id) => ({ label: entityById.get(id)?.label ?? humanize(id), to: entityById.has(id) ? groundworksEntityPath(id) : undefined })} /></dd></div>
          <div><dt>{copy("page.connected-work.tool.recipes-label")}</dt><dd><InlineLinks values={tool.recipeIds} resolve={(id) => ({ label: recipeById.get(id)?.label ?? humanize(id), to: integrationRecipePath(id) })} /></dd></div>
        </dl>
        {tool.workblockPath ? <p><a href={workblockSourceUrl(tool.workblockPath)} target="_blank" rel="noreferrer">{copy("page.connected-work.open-demo-workblock")}</a></p> : null}
      </section>
    </RecordShell>
  );
}

export function IntegrationSystemRecordPage() {
  const { systemId } = useParams();
  const { snapshot } = useFramework();
  const copy = useCopy();
  const integrations = snapshot.integrations;
  const system = integrations.systemProfiles.find((candidate) => candidate.id === systemId);
  if (!system) return <RecordAbsence kind="system profiles" />;
  const family = integrations.systemFamilies.find(({ id }) => id === system.familyId);
  const recipeById = new Map(integrations.recipes.map((recipe) => [recipe.id, recipe]));
  const channelById = new Map(integrations.channels.map((channel) => [channel.id, channel]));
  const formatById = new Map(integrations.formats.map((format) => [format.id, format]));
  const systemRoleById = new Map(integrations.systemRoles.map((role) => [role.id, role]));
  const domainById = new Map(snapshot.content.domainWorkblocks.map((domain) => [domain.id, domain]));
  return (
    <RecordShell
      eyebrow={family ? family.label : copy("page.connected-work.record.system-eyebrow", "System profile")}
      title={system.name}
    >
      <section className="apf-page-section">
        <dl className="apf-recipe-facts">
          <div><dt>{copy("page.connected-work.system.roles-label")}</dt><dd><InlineLinks values={system.systemRoleIds} resolve={(id) => ({ label: systemRoleById.get(id)?.label ?? humanize(id) })} /></dd></div>
          <div><dt>{copy("page.connected-work.system.recipes-label")}</dt><dd>{system.recipeIds.length ? <InlineLinks values={system.recipeIds} resolve={(id) => ({ label: recipeById.get(id)?.label ?? humanize(id), to: integrationRecipePath(id) })} /> : copy("page.connected-work.system.discovery-required")}</dd></div>
          <div><dt>{copy("page.connected-work.system.domains-label")}</dt><dd><InlineLinks values={system.domainWorkblockIds} resolve={(id) => ({ label: domainById.has(id) ? copy(domainById.get(id)!.nameKey) : humanize(id), to: domainById.has(id) ? domainWorkblockPath(id) : undefined })} /></dd></div>
          <div><dt>{copy("page.connected-work.system.channels-label")}</dt><dd><InlineLinks values={system.channelIds} resolve={(id) => ({ label: channelById.get(id)?.label ?? humanize(id) })} /></dd></div>
          <div><dt>{copy("page.connected-work.fact.formats")}</dt><dd><InlineLinks values={system.formatIds} resolve={(id) => ({ label: formatById.get(id)?.label ?? humanize(id) })} /></dd></div>
        </dl>
        <p><strong>{copy("page.connected-work.system.question-label")}</strong> {system.designQuestion}</p>
      </section>
    </RecordShell>
  );
}

export function IntegrationMappingRecordPage() {
  const { mappingId } = useParams();
  const { snapshot } = useFramework();
  const copy = useCopy();
  const integrations = snapshot.integrations;
  const profile = integrations.mappingProfiles.find((candidate) => candidate.id === mappingId);
  if (!profile) return <RecordAbsence kind="mapping profiles" />;
  const recipeById = new Map(integrations.recipes.map((recipe) => [recipe.id, recipe]));
  const channelById = new Map(integrations.channels.map((channel) => [channel.id, channel]));
  const formatById = new Map(integrations.formats.map((format) => [format.id, format]));
  const entityById = new Map(snapshot.groundworks.entities.map((entity) => [entity.id, entity]));
  const systemById = new Map(integrations.systemProfiles.map((system) => [system.id, system]));
  return (
    <RecordShell
      eyebrow={copy("page.connected-work.client-schema-label")}
      title={profile.label}
    >
      <section className="apf-page-section">
        <p>{profile.mappingBoundary}</p>
        <dl className="apf-recipe-facts">
          <div><dt>{copy("page.connected-work.mapping-systems-label")}</dt><dd><InlineLinks values={profile.systemIds} resolve={(id) => ({ label: systemById.get(id)?.name ?? humanize(id), to: systemById.has(id) ? integrationSystemPath(id) : undefined })} /></dd></div>
          <div><dt>{copy("page.connected-work.mapping-recipes-label")}</dt><dd><InlineLinks values={profile.recipeIds} resolve={(id) => ({ label: recipeById.get(id)?.label ?? humanize(id), to: integrationRecipePath(id) })} /></dd></div>
          <div><dt>{copy("page.connected-work.mapping-channels-label")}</dt><dd><InlineLinks values={profile.channelIds} resolve={(id) => ({ label: channelById.get(id)?.label ?? humanize(id) })} /></dd></div>
          <div><dt>{copy("page.connected-work.mapping-formats-label")}</dt><dd><InlineLinks values={profile.formatIds} resolve={(id) => ({ label: formatById.get(id)?.label ?? humanize(id) })} /></dd></div>
          <div><dt>{copy("page.connected-work.mapping-records-label")}</dt><dd><InlineLinks values={profile.entityIds} resolve={(id) => ({ label: entityById.get(id)?.label ?? humanize(id), to: groundworksEntityPath(id) })} /></dd></div>
        </dl>
      </section>
    </RecordShell>
  );
}

export function IntegrationStandardRecordPage() {
  const { formatId } = useParams();
  const { snapshot } = useFramework();
  const copy = useCopy();
  const integrations = snapshot.integrations;
  const format = integrations.formats.find((candidate) => candidate.id === formatId);
  if (!format) return <RecordAbsence kind="standards" />;
  const standard = format.standardId
    ? snapshot.groundworks.standards.find(({ id }) => id === format.standardId)
    : undefined;
  return (
    <RecordShell
      eyebrow={copy("page.connected-work.standards-title")}
      title={format.label}
    >
      <section className="apf-page-section">
        <dl className="apf-recipe-facts">
          <div><dt>{copy("page.connected-work.standard.role-label")}</dt><dd>{format.role}</dd></div>
          <div><dt>{copy("page.connected-work.standard.pinning-label")}</dt><dd>{format.pinning}</dd></div>
          {standard ? <>
            <div><dt>{copy("page.connected-work.standard.profile-label")}</dt><dd>{standard.label} · {standard.version}</dd></div>
            <div><dt>{copy("page.connected-work.standard.scope-label")}</dt><dd>{standard.scope}</dd></div>
          </> : <div><dt>{copy("page.connected-work.standard.profile-label")}</dt><dd>{copy("page.connected-work.client-pinned-format")}</dd></div>}
        </dl>
      </section>
    </RecordShell>
  );
}
